<template>
  <div class="manage-user-profile">
    <Card class="profile-form-card">
      <template #title>
        <h2 class="form-title">Complete Your Profile</h2>
        <p class="form-subtitle">Please provide your details to continue with your investment journey</p>
      </template>
      <template #content>
        <form @submit.prevent="handleSubmit" class="profile-form">
          <!-- Personal Information Section -->
          <div class="form-section">
            <h3 class="section-title">Personal Information</h3>

            <!-- Name Fields -->
            <div class="form-row">
              <div class="form-field">
                <label for="firstName" class="field-label">First Name *</label>
                <InputText
                  id="firstName"
                  v-model="formData.firstName"
                  :class="{'p-invalid': errors.firstName}"
                  placeholder="Enter your first name"
                  required
                />
                <small v-if="errors.firstName" class="error-message">{{ errors.firstName }}</small>
              </div>

              <div class="form-field">
                <label for="lastName" class="field-label">Last Name *</label>
                <InputText
                  id="lastName"
                  v-model="formData.lastName"
                  :class="{'p-invalid': errors.lastName}"
                  placeholder="Enter your last name"
                  required
                />
                <small v-if="errors.lastName" class="error-message">{{ errors.lastName }}</small>
              </div>
            </div>

            <!-- Date of Birth -->
            <div class="form-field">
              <label for="dob" class="field-label">Date of Birth *</label>
              <DatePicker
                id="dob"
                v-model="formData.dob"
                :class="{'p-invalid': errors.dob}"
                dateFormat="yy-mm-dd"
                :maxDate="maxDate"
                :yearRange="yearRange"
                placeholder="Select your date of birth"
                showIcon
                required
              />
              <small v-if="errors.dob" class="error-message">{{ errors.dob }}</small>
            </div>

            <!-- Marital Status -->
            <div class="form-field">
              <label for="maritalStatus" class="field-label">Marital Status *</label>
              <Select
                id="maritalStatus"
                v-model="formData.maritalStatus"
                :options="maritalStatusOptions"
                optionLabel="label"
                optionValue="value"
                :class="{'p-invalid': errors.maritalStatus}"
                placeholder="Select your marital status"
                required
              />
              <small v-if="errors.maritalStatus" class="error-message">{{ errors.maritalStatus }}</small>
            </div>

            <!-- PAN Number -->
            <div class="form-field">
              <label for="panNumber" class="field-label">PAN Number *</label>
              <InputText
                id="panNumber"
                v-model="formData.panNumber"
                :class="{'p-invalid': errors.panNumber}"
                placeholder="Enter your PAN (e.g., ABCDE1234F)"
                maxlength="10"
                @input="validatePANFormat"
                required
              />
              <small v-if="errors.panNumber" class="error-message">{{ errors.panNumber }}</small>
              <small v-else class="help-text">Required for KYC verification</small>
            </div>
          </div>

          <!-- Contact Information Section -->
          <div class="form-section">
            <h3 class="section-title">Contact Information</h3>

            <!-- Address -->
            <div class="form-field">
              <label for="address" class="field-label">Address *</label>
              <Textarea
                id="address"
                v-model="formData.address"
                :class="{'p-invalid': errors.address}"
                rows="3"
                placeholder="Enter your complete address"
                required
              />
              <small v-if="errors.address" class="error-message">{{ errors.address }}</small>
            </div>

            <!-- Phone Number -->
            <div class="form-row">
              <div class="form-field" style="flex: 0 0 120px;">
                <label for="phoneCountryCode" class="field-label">Country Code *</label>
                <Select
                  id="phoneCountryCode"
                  v-model="formData.phoneCountryCode"
                  :options="countryCodeOptions"
                  optionLabel="label"
                  optionValue="value"
                  :class="{'p-invalid': errors.phoneCountryCode}"
                  placeholder="Code"
                  required
                />
                <small v-if="errors.phoneCountryCode" class="error-message">{{ errors.phoneCountryCode }}</small>
              </div>

              <div class="form-field">
                <label for="phoneNumber" class="field-label">Phone Number *</label>
                <InputText
                  id="phoneNumber"
                  v-model="formData.phoneNumber"
                  :class="{'p-invalid': errors.phoneNumber}"
                  placeholder="Enter your phone number"
                  maxlength="10"
                  @input="validatePhoneNumber"
                  required
                />
                <small v-if="errors.phoneNumber" class="error-message">{{ errors.phoneNumber }}</small>
              </div>
            </div>
          </div>

          <!-- Optional Information Section -->
          <div class="form-section">
            <h3 class="section-title">Additional Information (Optional)</h3>

            <!-- CIBIL Score -->
            <div class="form-field">
              <label for="cibilScore" class="field-label">CIBIL Score</label>
              <InputNumber
                id="cibilScore"
                v-model="formData.cibilScore"
                :min="300"
                :max="900"
                placeholder="Enter your CIBIL score (300-900)"
              />
              <small class="help-text">Your credit score helps us provide better financial recommendations</small>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="form-actions">
            <ANButton
              type="submit"
              :loading="isSubmitting"
              :disabled="!isFormValid"
              label="Create Profile & Continue"
              size="large"
              class="submit-button"
            />
          </div>
        </form>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/store/user-store'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Textarea from 'primevue/textarea'
import DatePicker from 'primevue/datepicker'
import Select from 'primevue/select'
import ANButton from '@/components/common/AnButton.vue'

const store = useUserStore()

// Form data
const formData = ref({
  firstName: '',
  lastName: '',
  dob: null,
  maritalStatus: null,
  panNumber: '',
  address: '',
  phoneCountryCode: '91',
  phoneNumber: '',
  cibilScore: null
})

// Form validation errors
const errors = ref({})
const isSubmitting = ref(false)

// Form options
const maritalStatusOptions = [
  { label: 'Single', value: 'Single' },
  { label: 'Married', value: 'Married' }
]

const countryCodeOptions = [
  { label: '+91 (India)', value: '91' },
  { label: '+1 (USA)', value: '1' },
  { label: '+44 (UK)', value: '44' },
  { label: '+61 (Australia)', value: '61' },
  { label: '+65 (Singapore)', value: '65' }
]

// Date constraints
const maxDate = ref(new Date())
const yearRange = computed(() => {
  const currentYear = new Date().getFullYear()
  return `${currentYear - 80}:${currentYear - 18}`
})

// Form validation
const validateForm = () => {
  const newErrors = {}

  // Required field validations
  if (!formData.value.firstName?.trim()) {
    newErrors.firstName = 'First name is required'
  }

  if (!formData.value.lastName?.trim()) {
    newErrors.lastName = 'Last name is required'
  }

  if (!formData.value.dob) {
    newErrors.dob = 'Date of birth is required'
  } else {
    // Check if user is at least 18 years old
    const today = new Date()
    const birthDate = new Date(formData.value.dob)
    const age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (age < 18 || (age === 18 && monthDiff < 0) || (age === 18 && monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      newErrors.dob = 'You must be at least 18 years old'
    }
  }

  if (!formData.value.maritalStatus) {
    newErrors.maritalStatus = 'Marital status is required'
  }

  if (!formData.value.address?.trim()) {
    newErrors.address = 'Address is required'
  }

  if (!formData.value.panNumber?.trim()) {
    newErrors.panNumber = 'PAN number is required'
  } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.value.panNumber.trim())) {
    newErrors.panNumber = 'Invalid PAN format (e.g., ABCDE1234F)'
  }

  if (!formData.value.phoneCountryCode) {
    newErrors.phoneCountryCode = 'Country code is required'
  }

  if (!formData.value.phoneNumber?.trim()) {
    newErrors.phoneNumber = 'Phone number is required'
  } else if (!/^\d{10}$/.test(formData.value.phoneNumber.trim())) {
    newErrors.phoneNumber = 'Please enter a valid 10-digit phone number'
  }

  errors.value = newErrors
  return Object.keys(newErrors).length === 0
}

const validatePhoneNumber = (event) => {
  // Allow only numeric input
  const value = event.target.value.replace(/\D/g, '')
  formData.value.phoneNumber = value
}

const validatePANFormat = (event) => {
  // Convert to uppercase and allow only alphanumeric
  const value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
  formData.value.panNumber = value
}

const isFormValid = computed(() => {
  return formData.value.firstName?.trim() &&
         formData.value.lastName?.trim() &&
         formData.value.dob &&
         formData.value.maritalStatus &&
         formData.value.panNumber?.trim() &&
         /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.value.panNumber?.trim() || '') &&
         formData.value.address?.trim() &&
         formData.value.phoneCountryCode &&
         formData.value.phoneNumber?.trim() &&
         /^\d{10}$/.test(formData.value.phoneNumber?.trim() || '')
})

// Handle form submission
const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  try {
    isSubmitting.value = true

    // Format date to YYYY-MM-DD
    const formattedDate = formData.value.dob instanceof Date
      ? formData.value.dob.toISOString().split('T')[0]
      : formData.value.dob

    const payload = {
      firstName: formData.value.firstName.trim(),
      lastName: formData.value.lastName.trim(),
      dob: formattedDate,
      PAN: formData.value.panNumber.trim(),
      address: formData.value.address.trim(),
      phoneCountryCode: formData.value.phoneCountryCode,
      phoneNumber: formData.value.phoneNumber.trim()
    }

    console.log('Creating user profile with payload:', payload)

    const response = await store.createUserProfile(payload)

    if (response && response.success) {
      console.log('Profile created successfully:', response)

      // Reload dashboard data to get the new customerId
      await store.loadDashboardData(store.user.email)

      // After profile creation, navigate to KYC verification
      store.setTabView('KYCVerification')
      store.fireAlert('Success', 'Profile created successfully! Please complete KYC verification to continue.')
    } else {
      throw new Error(response?.message || 'Failed to create profile')
    }

  } catch (error) {
    console.error('Error creating profile:', error)
    store.fireAlert('Error', error.message || 'Failed to create profile. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  document.title = 'Complete Your Profile - AssetNivesh'
})
</script>

<style scoped>
.manage-user-profile {
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
}

.profile-form-card {
  box-shadow: var(--an-card-shadow);
}

.form-title {
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
  font-size: 1.8rem;
  font-weight: 700;
}

.form-subtitle {
  color: var(--an-text-secondary);
  margin: 0;
  font-size: 1rem;
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-title {
  color: var(--an-primary);
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--an-primary);
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.field-label {
  color: var(--an-text-primary);
  font-weight: 600;
  font-size: 14px;
}

.error-message {
  color: var(--an-error);
  font-size: 12px;
}

.help-text {
  color: var(--an-text-secondary);
  font-size: 12px;
  font-style: italic;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.submit-button {
  width: 300px;
}

/* PrimeVue component styling */
:deep(.p-inputtext),
:deep(.p-select),
:deep(.p-datepicker),
:deep(.p-inputnumber-input) {
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

:deep(.p-inputtext:focus),
:deep(.p-select:focus),
:deep(.p-datepicker:focus-within),
:deep(.p-inputnumber:focus-within) {
  border-color: var(--an-primary);
  box-shadow: 0 0 0 0.2rem rgba(0, 78, 80, 0.1);
}

:deep(.p-invalid) {
  border-color: var(--an-error) !important;
}

:deep(.p-datepicker .p-datepicker-trigger) {
  background: var(--an-primary);
  border-color: var(--an-primary);
}

/* Responsive design */
@media (max-width: 768px) {
  .manage-user-profile {
    padding: 16px;
  }

  .form-row {
    flex-direction: column;
  }

  .submit-button {
    width: 100%;
  }
}
</style>
